import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Compact numeric input. Renders as a small button showing the current
// value (e.g. "[ 5 ]"). Tapping it opens a cell-anchored 3-row scroll
// picker — same magnification feel as ScoreScrollPicker — for selecting
// a new value. Tap an item / tap outside / Enter commits and closes;
// Escape cancels.

// Format a wheel value for display: the zeroLabel for 0, whole numbers as-is,
// and fractional values to two decimals (so 0.5 reads "0.50"). Shared by the
// trigger button and the wheel items.
function fmtValue(n: number, zeroLabel?: string): string {
  if (n === 0 && zeroLabel !== undefined) return zeroLabel;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

interface Props {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  // Optional explicit value list. When provided it overrides min/max/step —
  // the wheel snaps through exactly these values (e.g. a sub-dollar bet set
  // like [0, 0.25, 0.5, 1, 2, 3, …]). Order is preserved as given.
  values?: number[];
  // Optional formatting for the trigger button only — does NOT affect
  // the values stored or passed to onChange.
  prefix?: string;
  suffix?: string;
  // When set, the number `0` is rendered as this label in both the
  // trigger and the wheel (e.g. "—" for a "no bet" affordance on
  // money inputs). The committed value is still numeric 0; this is
  // pure display sugar.
  zeroLabel?: string;
  ariaLabel?: string;
  className?: string;
}

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 3;
const WHEEL_WIDTH = 64;

export default function NumericPopoverPicker({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  values,
  prefix = "",
  suffix = "",
  zeroLabel,
  ariaLabel,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpen = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchorRect(rect);
    setOpen(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`cg-num-trigger${className ? " " + className : ""}`}
        onClick={handleOpen}
        aria-label={ariaLabel}
      >
        {prefix}
        {fmtValue(value, zeroLabel)}
        {suffix}
      </button>
      {open && anchorRect && (
        <Popover
          anchorRect={anchorRect}
          initial={value}
          min={min}
          max={max}
          step={step}
          values={values}
          zeroLabel={zeroLabel}
          onCommit={(n) => {
            if (n !== value) onChange(n);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ─── Floating popover ───────────────────────────────────────────────────────

interface PopoverProps {
  anchorRect: DOMRect;
  initial: number;
  min: number;
  max: number;
  step: number;
  values?: number[];
  zeroLabel?: string;
  onCommit: (next: number) => void;
  onCancel: () => void;
}

function Popover({
  anchorRect,
  initial,
  min,
  max,
  step,
  values,
  zeroLabel,
  onCommit,
  onCancel,
}: PopoverProps) {
  const items = useMemo<number[]>(() => {
    if (values && values.length) return values;
    const out: number[] = [];
    for (let n = min; n <= max; n += step) out.push(n);
    return out;
  }, [min, max, step, values]);

  // Snap the displayed initial value to the nearest item in the range —
  // catches cases where a stored option's default doesn't sit on a step.
  const initialIdx = (() => {
    const idx = items.indexOf(initial);
    if (idx >= 0) return idx;
    const closest = items.reduce(
      (best, n, i) => (Math.abs(n - initial) < Math.abs(items[best] - initial) ? i : best),
      0,
    );
    return closest;
  })();

  const [value, setValue] = useState<number>(items[initialIdx] ?? initial);
  const valueRef = useRef(value);
  valueRef.current = value;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  // One frame of "armed: false" so the tap that opened the popover
  // doesn't immediately fire the outside-click commit.
  const armedRef = useRef(false);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = initialIdx * ITEM_HEIGHT;
    const t = setTimeout(() => {
      armedRef.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Debounced "scroll settled" → update visible value.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
        const clamped = Math.max(0, Math.min(items.length - 1, idx));
        const next = items[clamped];
        if (next !== undefined && next !== valueRef.current) setValue(next);
      }, 80);
    };
    el.addEventListener("scroll", handle, { passive: true });
    return () => {
      el.removeEventListener("scroll", handle);
      if (timer) clearTimeout(timer);
    };
  }, [items]);

  // Outside click / Enter → commit; Escape → cancel.
  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!armedRef.current) return;
      const target = e.target as Node;
      if (overlayRef.current && overlayRef.current.contains(target)) return;
      onCommit(valueRef.current);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") onCommit(valueRef.current);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [onCommit, onCancel]);

  // Single-tap commit, matching ScoreScrollPicker — tap any visible
  // item and it's the chosen value, picker closes. (Previously this
  // required a tap-to-centre then a tap-to-confirm, which diverged
  // from the per-hole score gesture users already know.)
  const onItemClick = (n: number) => {
    onCommit(n);
  };

  // Position centred over the trigger, clamped inside the viewport.
  const wheelH = VISIBLE_COUNT * ITEM_HEIGHT;
  const padding = 12;
  const pickerW = WHEEL_WIDTH + padding * 2;
  const pickerH = wheelH + padding * 2;

  const cx = anchorRect.left + anchorRect.width / 2;
  const cy = anchorRect.top + anchorRect.height / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = cx - pickerW / 2;
  let top = cy - pickerH / 2;
  if (left < 4) left = 4;
  if (left + pickerW > vw - 4) left = vw - 4 - pickerW;
  if (top < 4) top = 4;
  if (top + pickerH > vh - 4) top = vh - 4 - pickerH;

  return (
    <div className="cg-score-pick-backdrop" aria-hidden>
      <div
        ref={overlayRef}
        className="cg-score-pick"
        role="dialog"
        aria-label="Pick a value"
        style={{ left, top, width: pickerW, height: pickerH }}
      >
        <div
          className="cg-score-pick-wheel"
          style={{ width: WHEEL_WIDTH, height: wheelH }}
        >
          <div
            className="cg-score-pick-indicator"
            style={{ top: ITEM_HEIGHT, height: ITEM_HEIGHT }}
          />
          <div
            ref={scrollerRef}
            className="cg-score-pick-scroller"
            style={{ height: wheelH }}
          >
            <div style={{ height: ITEM_HEIGHT }} aria-hidden />
            {items.map((n) => (
              <div
                key={n}
                className={`cg-score-pick-item${
                  n === value ? " cg-score-pick-item-on" : ""
                }`}
                style={{ height: ITEM_HEIGHT }}
                onClick={() => onItemClick(n)}
              >
                {fmtValue(n, zeroLabel)}
              </div>
            ))}
            <div style={{ height: ITEM_HEIGHT }} aria-hidden />
          </div>
        </div>
      </div>
    </div>
  );
}
